import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg, fmtSlot } from "@/lib/api";
import { SectionHead } from "@/components/site/SectionHead";
import { useLang } from "@/context/LangContext";

const LOCALE = { en: "en-IN", hi: "hi-IN", kn: "kn-IN" };

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const upcomingWeekends = () => {
  const out = [];
  const d = new Date();
  while (out.length < 6) {
    if (d.getDay() === 6 || d.getDay() === 0) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
};

export default function Booking() {
  const days = useMemo(upcomingWeekends, []);
  const { t, lang } = useLang();
  const loc = LOCALE[lang];
  const [day, setDay] = useState(iso(days[0]));
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const loadSlots = (d) => {
    setSlots(null);
    api.get("/slots", { params: { date: d } }).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  };

  useEffect(() => {
    setTime("");
    loadSlots(day);
  }, [day]);

  const submit = async (e) => {
    e.preventDefault();
    if (!time) return toast.error(t("bk.pick"));
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { ...form, date: day, time });
      setDone(data);
      setForm({ name: "", phone: "", reason: "" });
      loadSlots(day);
    } catch (err) {
      toast.error(errMsg(err));
      loadSlots(day);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="booking" className="max-w-6xl mx-auto px-5 py-20 sm:py-28">
      <SectionHead eyebrow={t("bk.eyebrow")} title={t("bk.title")} text={t("bk.text")} />
      {done ? (
        <div className="bg-white border border-parchment-300 rounded-2xl p-8 max-w-xl" data-testid="booking-success">
          <CheckCircle2 className="w-10 h-10 text-forest-700" />
          <h3 className="mt-4 text-3xl font-semibold text-forest-800">{t("bk.doneTitle")}</h3>
          <p className="mt-2 text-[#57645A]">{t("bk.doneText", { d: new Date(`${done.date}T00:00:00`).toLocaleDateString(loc, { weekday: "long", day: "numeric", month: "long" }), t: fmtSlot(done.time), p: done.phone })}</p>
          <button onClick={() => setDone(null)} className="mt-6 text-sm font-semibold text-brass-700 underline underline-offset-4" data-testid="booking-another-button">{t("bk.another")}</button>
        </div>
      ) : (
        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-8">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-forest-800 mb-3">{t("bk.s1")}</p>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-5 px-5 sm:mx-0 sm:px-0">
              {days.map((d) => {
                const v = iso(d);
                return (
                  <button type="button" key={v} onClick={() => setDay(v)} data-testid={`booking-day-${v}`}
                    className={`shrink-0 w-20 py-3 rounded-xl border text-center transition-colors ${day === v ? "bg-forest-800 text-parchment border-forest-800" : "bg-white border-parchment-300 hover:border-brass-500"}`}>
                    <span className="block text-[10px] uppercase tracking-widest">{d.toLocaleDateString(loc, { weekday: "short" })}</span>
                    <span className="block font-serif text-2xl font-bold">{d.getDate()}</span>
                    <span className="block text-[10px]">{d.toLocaleDateString(loc, { month: "short" })}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-sm font-semibold text-forest-800 mt-7 mb-3">{t("bk.s2")}</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-slot-grid">
              {!slots && <Loader2 className="w-5 h-5 animate-spin text-brass-700" />}
              {slots?.map((s) => (
                <button type="button" key={s.time} disabled={!s.available} onClick={() => setTime(s.time)} data-testid={`booking-slot-${s.time}`}
                  className={`py-2.5 rounded-lg text-sm border transition-colors ${!s.available ? "bg-parchment-200 text-[#8A978E] line-through border-transparent cursor-not-allowed" : time === s.time ? "bg-brass-500 border-brass-500 text-forest-900 font-semibold" : "bg-white border-parchment-300 hover:border-forest-700"}`}>
                  {fmtSlot(s.time)}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-parchment-300 rounded-2xl p-6 sm:p-7 space-y-4 h-fit">
            <p className="text-sm font-semibold text-forest-800">{t("bk.s3")}</p>
            <div className="space-y-1.5"><Label>{t("co.name")}</Label><Input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name-input" /></div>
            <div className="space-y-1.5"><Label>{t("co.phone")}</Label><Input required inputMode="numeric" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone-input" /></div>
            <div className="space-y-1.5"><Label>{t("bk.reason")}</Label><Textarea rows={3} placeholder={t("bk.reasonPh")} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="booking-reason-input" /></div>
            <button disabled={busy} className="w-full flex items-center justify-center gap-2 bg-forest-800 hover:bg-forest-700 text-parchment font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60" data-testid="booking-submit-button">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {time ? t("bk.bookAt", { t: fmtSlot(time) }) : t("bk.book")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
